import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  message,
  Tooltip,
  Card,
  Statistic,
  Row,
  Col,
  Popconfirm,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CodeOutlined,
  BarChartOutlined,
  MessageOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import { getWidgets, deleteWidget, Widget } from '../lib/widgetsApi';
import CreateWidgetModal from '../components/Widgets/CreateWidgetModal';
import EditWidgetModal from '../components/Widgets/EditWidgetModal';
import WidgetEmbedModal from '../components/Widgets/WidgetEmbedModal';
import WidgetAnalyticsModal from '../components/Widgets/WidgetAnalyticsModal';
import WidgetConversationsModal from '../components/Widgets/WidgetConversationsModal';

const WidgetsPage: React.FC = () => {
  const [widgets, setWidgets] = useState<Widget[]>([]);
  const [loading, setLoading] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [embedModalVisible, setEmbedModalVisible] = useState(false);
  const [analyticsModalVisible, setAnalyticsModalVisible] = useState(false);
  const [conversationsModalVisible, setConversationsModalVisible] = useState(false);
  const [selectedWidget, setSelectedWidget] = useState<Widget | null>(null);

  useEffect(() => {
    loadWidgets();
  }, []);

  const loadWidgets = async () => {
    setLoading(true);
    try {
      const data = await getWidgets();
      setWidgets(data);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to load widgets');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (widgetId: string) => {
    try {
      await deleteWidget(widgetId);
      message.success('Widget deleted successfully');
      loadWidgets();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to delete widget');
    }
  };

  const handleCopyWidgetKey = (widgetKey: string) => {
    navigator.clipboard.writeText(widgetKey);
    message.success('Widget key copied to clipboard');
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: Widget) => (
        <div>
          <div className="font-medium">{text}</div>
          {record.description && (
            <div className="text-xs text-gray-500">{record.description}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Widget Key',
      dataIndex: 'widgetKey',
      key: 'widgetKey',
      render: (key: string) => (
        <Space>
          <code className="bg-gray-100 px-2 py-1 rounded text-xs">{key}</code>
          <Tooltip title="Copy widget key">
            <Button
              size="small"
              icon={<CopyOutlined />}
              onClick={() => handleCopyWidgetKey(key)}
            />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: 'Project',
      dataIndex: ['project', 'name'],
      key: 'project',
    },
    {
      title: 'Source Type',
      key: 'sourceType',
      render: (_: any, record: Widget) => {
        const sourceType = (record.authConfig as any)?.sourceType || 'website';
        const sourceColors: Record<string, string> = {
          website: 'blue',
          teams: 'purple',
          maximo: 'orange',
          slack: 'magenta',
          'custom-sso': 'cyan',
        };
        const sourceLabels: Record<string, string> = {
          website: 'Website',
          teams: 'Teams',
          maximo: 'Maximo',
          slack: 'Slack',
          'custom-sso': 'Custom SSO',
        };
        return (
          <Tag color={sourceColors[sourceType] || 'default'}>
            {sourceLabels[sourceType] || sourceType}
          </Tag>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>{isActive ? 'Active' : 'Inactive'}</Tag>
      ),
    },
    {
      title: 'Conversations',
      dataIndex: 'totalConversations',
      key: 'totalConversations',
      align: 'center' as const,
    },
    {
      title: 'Messages',
      dataIndex: 'totalMessages',
      key: 'totalMessages',
      align: 'center' as const,
    },
    {
      title: 'Last Used',
      dataIndex: 'lastUsedAt',
      key: 'lastUsedAt',
      render: (date: string) =>
        date ? new Date(date).toLocaleDateString() : 'Never',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Widget) => (
        <Space>
          <Tooltip title="Get Embed Code">
            <Button
              size="small"
              icon={<CodeOutlined />}
              onClick={() => {
                setSelectedWidget(record);
                setEmbedModalVisible(true);
              }}
            />
          </Tooltip>
          <Tooltip title="View Analytics">
            <Button
              size="small"
              icon={<BarChartOutlined />}
              onClick={() => {
                setSelectedWidget(record);
                setAnalyticsModalVisible(true);
              }}
            />
          </Tooltip>
          <Tooltip title="View Conversations">
            <Button
              size="small"
              icon={<MessageOutlined />}
              onClick={() => {
                setSelectedWidget(record);
                setConversationsModalVisible(true);
              }}
            />
          </Tooltip>
          <Tooltip title="Edit Widget">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                setSelectedWidget(record);
                setEditModalVisible(true);
              }}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this widget?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete Widget">
              <Button size="small" icon={<DeleteOutlined />} danger />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Calculate summary statistics
  const totalConversations = widgets.reduce((sum, w) => sum + w.totalConversations, 0);
  const totalMessages = widgets.reduce((sum, w) => sum + w.totalMessages, 0);
  const activeWidgets = widgets.filter((w) => w.isActive).length;

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold mb-1">Chat Widgets</h1>
            <p className="text-gray-600">
              Create and manage embeddable chat widgets for your websites
            </p>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateModalVisible(true)}
          >
            Create Widget
          </Button>
        </div>

        {/* Summary Statistics */}
        <Row gutter={16} className="mb-6">
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Widgets"
                value={widgets.length}
                valueStyle={{ color: '#13afe3' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Active Widgets"
                value={activeWidgets}
                valueStyle={{ color: '#10B981' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Conversations"
                value={totalConversations}
                valueStyle={{ color: '#6366f1' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Messages"
                value={totalMessages}
                valueStyle={{ color: '#F59E0B' }}
              />
            </Card>
          </Col>
        </Row>
      </div>

      <Table
        columns={columns}
        dataSource={widgets}
        rowKey="id"
        loading={loading}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} widgets`,
        }}
      />

      {/* Create Widget Modal */}
      <CreateWidgetModal
        visible={createModalVisible}
        onClose={() => setCreateModalVisible(false)}
        onSuccess={() => {
          setCreateModalVisible(false);
          loadWidgets();
        }}
      />

      {/* Edit Widget Modal */}
      {selectedWidget && (
        <EditWidgetModal
          visible={editModalVisible}
          widget={selectedWidget}
          onClose={() => {
            setEditModalVisible(false);
            setSelectedWidget(null);
          }}
          onSuccess={() => {
            setEditModalVisible(false);
            setSelectedWidget(null);
            loadWidgets();
          }}
        />
      )}

      {/* Embed Code Modal */}
      {selectedWidget && (
        <WidgetEmbedModal
          visible={embedModalVisible}
          widgetId={selectedWidget.id}
          onClose={() => {
            setEmbedModalVisible(false);
            setSelectedWidget(null);
          }}
        />
      )}

      {/* Analytics Modal */}
      {selectedWidget && (
        <WidgetAnalyticsModal
          visible={analyticsModalVisible}
          widgetId={selectedWidget.id}
          onClose={() => {
            setAnalyticsModalVisible(false);
            setSelectedWidget(null);
          }}
        />
      )}

      {/* Conversations Modal */}
      {selectedWidget && (
        <WidgetConversationsModal
          visible={conversationsModalVisible}
          widgetId={selectedWidget.id}
          onClose={() => {
            setConversationsModalVisible(false);
            setSelectedWidget(null);
          }}
        />
      )}
    </div>
  );
};

export default WidgetsPage;
